import { makeAutoObservable, reaction, when, comparer } from 'mobx';
import {
  AccountNotFoundError,
  NizzaAccount,
  PrivateNizzaAccount,
  getAccount,
} from '../accounts';
import { NizzaDB } from '../dexie/nizza-db';
import { BroadcastChannelEventBus } from '../event-buses';
import { createPinoLogger } from '../logger';
import {
  AccountStatus,
  AddToCartConfig,
  INizzaStore,
  NizzaConfig,
  NizzaProductDataSource,
} from '../types';
import { shortId } from '../utils/helpers';

export class NizzaStore implements INizzaStore {
  static readonly version = '11';
  private static instanceRef: NizzaStore;

  readonly projectName = 'nizza';
  readonly instanceId = shortId();
  readonly db = new NizzaDB();
  readonly baseLogger = createPinoLogger(this.projectName, this.db);
  readonly coreLogger = this.baseLogger.child('core');
  readonly logger = this.coreLogger.child('NizzaStore');
  readonly eventBus = new BroadcastChannelEventBus(
    this.projectName,
    this.coreLogger,
  );

  private cfg: NizzaConfig | null = null;
  private accountData?: NizzaAccount | PrivateNizzaAccount;
  private statusInternal: AccountStatus = 'idle';
  private currentFetchId: string | null = null;
  private pds?: NizzaProductDataSource;

  private constructor() {
    makeAutoObservable(
      this,
      {
        projectName: false,
        instanceId: false,
        db: false,
        logger: false,
        coreLogger: false,
        eventBus: false,
      },
      { autoBind: true },
    );

    // Debounce account load only when account or token truly change
    reaction(
      () => [this.cfg?.account, this.cfg?.authToken],
      ([acct, token]) => {
        if (acct) this.loadAccount(acct, !!token);
      },
      {
        delay: 500,
        equals: comparer.shallow,
      },
    );
  }

  /**
   * Returns the singleton instance.
   */
  static instance(): NizzaStore {
    const win = window as any;
    if (!win.nizza?.store) {
      NizzaStore.instanceRef = new NizzaStore();
      NizzaStore.instanceRef.logger.debug('Created NizzaStore');
    } else {
      NizzaStore.instanceRef = win.nizza.store;
    }
    win.nizza = win.nizza || {};
    win.nz = win.nz || win.nizza;
    win.nizza.store = NizzaStore.instanceRef;
    win.nz = win.nizza;
    return NizzaStore.instanceRef;
  }

  /**
   * Initializes config and triggers account load.
   * @param config Full NizzaConfig
   */
  async configure(config: NizzaConfig): Promise<void> {
    try {
      this.logger.debug('Configuring NizzaStore', config);
      await this.applyConfig(config);
    } catch (err) {
      this.setStatus('error');
      this.logger.error('Configuration error', err);
    }
  }

  /**
   * Updates configuration and sets product source.
   * @param config Partial<NizzaConfig>
   */
  private async applyConfig(config: Partial<NizzaConfig> = {}) {
    this.cfg = { ...this.cfg, ...config } as NizzaConfig;
    if (config.getProductDataSource) {
      this.setProductDataSource(await config.getProductDataSource());
    }
  }

  /**
   * Loads account from API with supersede check.
   * @param name Account identifier
   * @param isPrivate Fetch private account if true
   */
  private async loadAccount(name: string, isPrivate?: boolean) {
    this.setStatus('loading');
    const fetchId = shortId();
    this.currentFetchId = fetchId;
    this.logger.setMetadata({ account: name });

    try {
      const account = await getAccount(name, isPrivate);
      if (this.currentFetchId !== fetchId) return; // ignore stale
      this.setAccount(account);
      this.setStatus('success');
      this.logger.debug('Account loaded', account);
    } catch (err) {
      if (this.currentFetchId !== fetchId) return;
      this.setStatus('error');
      if (err instanceof AccountNotFoundError) {
        this.logger.fatal('Account not found', name).options({ silent: true });
      } else {
        this.logger.fatal('Error loading account', err);
      }
    }
  }

  /** @param account NizzaAccount | PrivateNizzaAccount */
  private setAccount(account: NizzaAccount | PrivateNizzaAccount) {
    this.accountData = account;
  }

  /** @param status AccountStatus */
  private setStatus(status: AccountStatus) {
    this.statusInternal = status;
  }

  /** @param source NizzaProductDataSource */
  setProductDataSource(source: NizzaProductDataSource) {
    this.pds = source;
  }

  /** @param id Product identifier */
  setProductId(id: string) {
    if (!this.cfg) return;
    this.cfg.productId = id;
  }

  /** @param id Order form identifier */
  setOrderFormId(id: string) {
    if (!this.cfg) return;
    this.cfg.orderFormId = id;
  }

  /** @param config AddToCartConfig - must call configure() first */
  setAddToCartConfig(config: AddToCartConfig) {
    if (!this.cfg) throw new Error('Call configure() first');
    this.cfg.addToCartConfig = config;
  }

  /** @param token Authentication token */
  setAuthToken(token: string) {
    this.applyConfig({ authToken: token });
  }

  /** Forces reload of current account */
  async refetchAccount(): Promise<void> {
    if (!this.cfg?.account) return;
    await this.loadAccount(this.cfg.account, !!this.cfg.authToken);
  }

  /**
   * Reset account state and related config fields to their initial values.
   */
  public resetAccount(): void {
    this.accountData = undefined;
    this.statusInternal = 'idle';
    this.currentFetchId = null;
    this.pds = undefined;

    if (this.cfg) {
      this.cfg.account = undefined;
      this.cfg.authToken = undefined;
      this.cfg.productId = null;
      this.cfg.orderFormId = null;
    }

    this.logger.debug(
      'Account state and related config fields have been reset',
    );
  }

  /** @returns Current version */
  get version(): string {
    return NizzaStore.version;
  }

  /** @returns Active configuration */
  get config(): NizzaConfig | null {
    return this.cfg;
  }

  /** @returns Loaded account data */
  get account(): NizzaAccount | PrivateNizzaAccount | undefined {
    return this.accountData;
  }

  /** @returns Current fetch status */
  get accountStatus(): AccountStatus {
    return this.statusInternal;
  }

  /** @returns Configured account name */
  get accountName(): string | undefined {
    return this.cfg?.account;
  }

  /** @returns True if using private account */
  isPvtAccount(
    account: NizzaAccount | PrivateNizzaAccount | undefined,
  ): account is PrivateNizzaAccount {
    return Boolean(account && this.cfg?.authToken);
  }

  /** @returns True if using public account */
  isPubAccount(
    account: NizzaAccount | PrivateNizzaAccount | undefined,
  ): account is NizzaAccount {
    return Boolean(account && !this.cfg?.authToken);
  }

  /** @returns Product data source */
  get productDataSource(): NizzaProductDataSource | undefined {
    return this.pds;
  }
}

/**
 * Returns singleton NizzaStore and invokes onInit when account is ready.
 * @param onInit Callback after first account load
 */
export const getNizza = (onInit?: (store: NizzaStore) => void): NizzaStore => {
  const store = NizzaStore.instance();
  when(
    () => !!store.account,
    () => onInit?.(store),
  );
  return store;
};
